import Events from '@/events/events'

/**
 * Service de chat principal
 * Intégré avec le système événementiel existant
 */
export class ChatService {
  constructor(config = {}) {
    this.config = {
      apiEndpoint: '/api/chat',
      wsEndpoint: 'wss://api.example.com/chat',
      maxFileSize: 10 * 1024 * 1024, // 10MB
      allowedFileTypes: ['image/*', 'application/pdf', 'text/*'],
      enableReactions: true,
      enableTypingIndicators: true,
      ...config
    }

    // Handlers personnalisables pour les requêtes réseau
    this.handlers = {
      sendMessage: config.sendMessageHandler || null,
      fetchMessages: config.fetchMessagesHandler || null,
      markAsRead: config.markAsReadHandler || null,
      startTyping: config.startTypingHandler || null,
      stopTyping: config.stopTypingHandler || null
    }

    this.conversations = new Map()
    this.currentConversation = null
    this.currentUser = null
    this.typingTimeouts = new Map()
    this.isConnected = false

    this.init()
  }

  init() {
    // S'assurer que le système d'événements est initialisé
    if (typeof window !== 'undefined' && !window.customEventsHandlers) {
      Events.init()
    }

    // Écouter les événements globaux
    try {
      Events.handle('USER_CONNECTED', (userData) => {
        this.currentUser = {
          id: userData.id,
          name: userData.name,
          avatar: userData.avatar,
          role: userData.role || 'user'
        }
        this.connect()
      })

      Events.handle('USER_DISCONNECTED', () => {
        this.disconnect()
      })
    } catch (error) {
      console.warn('Failed to setup event handlers in ChatService:', error)
      // Continuer quand même sans les événements pour le mode développement
      console.log('ChatService initialized without event system (development mode)')
    }

    // Auto-connexion en mode développement
    if (this.isDevelopmentMode()) {
      setTimeout(() => {
        this.connect()
      }, 100)
    }
  }

  // Gestion de la connexion
  async connect() {
    try {
      this.isConnected = true
      Events.dispatch('chat:ready', { config: this.config })

      // Initialiser WebSocket si configuré
      if (this.config.wsEndpoint) {
        this.initWebSocket()
      }

      console.log('Chat service connected')
    } catch (error) {
      console.error('Failed to connect chat service:', error)
      Events.dispatch('chat:error', { error })

      // En mode développement, se connecter quand même
      if (this.isDevelopmentMode()) {
        console.log('Chat service connected in development mode')
        this.isConnected = true
      }
    }
  }

  disconnect() {
    this.isConnected = false
    this.currentConversation = null

    // Nettoyer les timeouts de typing
    this.typingTimeouts.forEach(timeout => clearTimeout(timeout))
    this.typingTimeouts.clear()

    console.log('Chat service disconnected')
  }

  initWebSocket() {
    // Implémentation WebSocket si nécessaire
    // Pour l'instant, on utilise le système événementiel existant
  }

  // Vérifier si on est en mode développement
  isDevelopmentMode() {
    return (
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('192.168.') ||
      !this.hasRealBackend()
    )
  }

  // Vérifier si on a un vrai backend
  hasRealBackend() {
    // Vérifier si les actions de fetch sont configurées
    return this.config.fetchActions &&
           Object.keys(this.config.fetchActions).length > 0 &&
           this.config.apiEndpoint &&
           !this.config.apiEndpoint.includes('/api/test')
  }

  // Gestion des conversations
  async createConversation(participants, type = 'direct', metadata = {}) {
    // En mode développement, permettre la création sans connexion
    if (!this.isConnected && !this.isDevelopmentMode()) {
      throw new Error('Chat service not connected')
    }

    const conversation = {
      id: this.generateId(),
      type,
      participants: Array.isArray(participants) ? participants : [participants],
      metadata,
      createdAt: new Date(),
      updatedAt: new Date(),
      unreadCount: 0
    }

    // Générer le nom pour les conversations directes
    if (type === 'direct' && participants.length === 1) {
      const otherUser = participants[0]
      conversation.name = otherUser.name
    }

    this.conversations.set(conversation.id, conversation)

    Events.dispatch('conversation:joined', { conversation })
    return conversation
  }

  async joinConversation(conversationId) {
    const conversation = this.conversations.get(conversationId)
    if (!conversation) {
      throw new Error('Conversation not found')
    }

    this.currentConversation = conversation
    Events.dispatch('conversation:joined', { conversation })

    // Récupérer les messages historiques
    await this.fetchMessages(conversationId)
  }

  async leaveConversation(conversationId) {
    this.currentConversation = null
    Events.dispatch('conversation:left', { conversationId })
  }

  // Gestion des messages
  async sendMessage(conversationId, text, attachments = [], replyTo = null) {
    if (!this.isConnected || !this.currentUser) {
      throw new Error('Cannot send message: not connected')
    }

    const message = {
      id: this.generateId(),
      text: text.trim(),
      userId: this.currentUser.id,
      userName: this.currentUser.name,
      userAvatar: this.currentUser.avatar,
      timestamp: new Date(),
      type: 'text',
      status: 'sending',
      attachments,
      replyTo,
      metadata: {}
    }

    try {
      // Utiliser les handlers personnalisés en priorité
      if (this.handlers.sendMessage) {
        console.log('Using custom sendMessage handler')
        const result = await this.handlers.sendMessage({
          conversationId,
          message,
          text,
          attachments,
          replyTo
        })

        // Mettre à jour le message avec la réponse du handler
        if (result.success !== false) {
          message.status = result.status || 'sent'
          message.id = result.messageId || message.id
          message.timestamp = new Date(result.timestamp || message.timestamp)
        } else {
          message.status = 'failed'
          throw new Error(result.error || 'Failed to send message')
        }
      }
      // Utiliser le système de fetch configuré en fallback
      else if (this.config.fetchActions?.sendMessage) {
        console.log('Using fetch action sendMessage')
        const result = await this.executeFetchAction(
          this.config.fetchActions.sendMessage,
          { conversationId, message }
        )

        // Mettre à jour le message avec la réponse du serveur
        if (result.success) {
          message.status = 'sent'
          message.id = result.messageId || message.id
          message.timestamp = new Date(result.timestamp || message.timestamp)
        } else {
          message.status = 'failed'
          throw new Error(result.error || 'Failed to send message')
        }
      } else {
        // Simulation en local pour le développement
        console.log('Using mock sendMessage (development mode)')
        setTimeout(() => {
          message.status = 'delivered'
          Events.dispatch('message:updated', { message, conversationId })
        }, 500)
      }

      // Mettre à jour la conversation
      const conversation = this.conversations.get(conversationId)
      if (conversation) {
        conversation.lastMessage = message
        conversation.updatedAt = new Date()
      }

      Events.dispatch('message:new', { message, conversationId })
      return message

    } catch (error) {
      message.status = 'failed'
      Events.dispatch('chat:error', { error, context: 'sendMessage' })
      throw error
    }
  }

  async fetchMessages(conversationId, limit = 50, before = null) {
    try {
      let messages = []

      if (this.config.fetchActions?.fetchMessages) {
        const result = await this.executeFetchAction(
          this.config.fetchActions.fetchMessages,
          { conversationId, limit, before }
        )
        messages = result.messages || []
      } else {
        // Simulation pour le développement
        messages = this.generateMockMessages(conversationId, limit)
      }

      // Traiter les messages reçus
      messages.forEach(message => {
        Events.dispatch('message:new', { message, conversationId })
      })

      return messages
    } catch (error) {
      Events.dispatch('chat:error', { error, context: 'fetchMessages' })
      throw error
    }
  }

  async markAsRead(conversationId, messageId) {
    try {
      if (this.config.fetchActions?.markAsRead) {
        await this.executeFetchAction(
          this.config.fetchActions.markAsRead,
          { conversationId, messageId }
        )
      }

      Events.dispatch('message:read', { messageId, userId: this.currentUser.id })

      // Mettre à jour le compteur de non lus
      const conversation = this.conversations.get(conversationId)
      if (conversation) {
        conversation.unreadCount = Math.max(0, (conversation.unreadCount || 0) - 1)
      }
    } catch (error) {
      Events.dispatch('chat:error', { error, context: 'markAsRead' })
    }
  }

  // Gestion des indicateurs de typing
  async startTyping(conversationId) {
    if (!this.config.enableTypingIndicators || !this.currentUser) return

    try {
      if (this.config.fetchActions?.startTyping) {
        await this.executeFetchAction(
          this.config.fetchActions.startTyping,
          { conversationId }
        )
      }

      Events.dispatch('conversation:typing', {
        userId: this.currentUser.id,
        conversationId,
        isTyping: true
      })

      // Auto-stop après 3 secondes
      const timeout = setTimeout(() => {
        this.stopTyping(conversationId)
      }, 3000)

      this.typingTimeouts.set(conversationId, timeout)
    } catch (error) {
      console.error('Failed to start typing indicator:', error)
    }
  }

  async stopTyping(conversationId) {
    if (!this.config.enableTypingIndicators || !this.currentUser) return

    // Nettoyer le timeout
    const timeout = this.typingTimeouts.get(conversationId)
    if (timeout) {
      clearTimeout(timeout)
      this.typingTimeouts.delete(conversationId)
    }

    try {
      if (this.config.fetchActions?.stopTyping) {
        await this.executeFetchAction(
          this.config.fetchActions.stopTyping,
          { conversationId }
        )
      }

      Events.dispatch('conversation:typing', {
        userId: this.currentUser.id,
        conversationId,
        isTyping: false
      })
    } catch (error) {
      console.error('Failed to stop typing indicator:', error)
    }
  }

  // Utilitaires
  generateId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  async executeFetchAction(actionName, params) {
    // Adapter ce code selon votre système de fetch existant
    // C'est un exemple basé sur les patterns que j'ai observés
    try {
      // Simulation - à adapter selon votre architecture
      console.log(`Executing fetch action: ${actionName}`, params)

      // En réalité, vous utiliseriez probablement :
      // const result = await someFetchService[actionName](params)
      // return result

      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  generateMockMessages(conversationId, count = 10) {
    const messages = []
    const now = new Date()

    for (let i = 0; i < count; i++) {
      const timestamp = new Date(now.getTime() - (i * 5 * 60 * 1000)) // 5 minutes d'intervalle
      const isOwn = i % 3 === 0 // 1 message sur 3 est du user actuel

      messages.push({
        id: this.generateId(),
        text: this.getMockMessageText(i),
        userId: isOwn ? this.currentUser?.id : `user_${i}`,
        userName: isOwn ? this.currentUser?.name : `User ${i}`,
        timestamp,
        type: 'text',
        status: 'read',
        attachments: [],
        reactions: Math.random() > 0.7 ? [{
          emoji: ['👍', '❤️', '😂', '👏'][Math.floor(Math.random() * 4)],
          userIds: [this.currentUser?.id],
          count: 1
        }] : []
      })
    }

    return messages.reverse()
  }

  getMockMessageText(index) {
    const texts = [
      "Salut ! Comment ça va ?",
      "Les nouvelles fonctionnalités sont vraiment géniales",
      "As-tu vu la dernière mise à jour ?",
      "Je peux t'aider avec ça",
      "Merci pour ton retour !",
      "On se voit plus tard ?",
      "N'hésite pas si tu as des questions",
      "Super travail sur ce projet !",
      "Les documents sont prêts",
      "À quelle heure est la réunion ?"
    ]

    return texts[index % texts.length]
  }

  // Getters
  getConversations() {
    return Array.from(this.conversations.values())
  }

  getConversation(id) {
    return this.conversations.get(id)
  }

  getCurrentConversation() {
    return this.currentConversation
  }

  isConnectedToChat() {
    return this.isConnected
  }
}

// Instance singleton - initialisation différée
let chatServiceInstance = null

export const chatService = {
  getInstance: () => {
    if (!chatServiceInstance) {
      chatServiceInstance = new ChatService()
    }
    return chatServiceInstance
  },

  // Proxy pour accéder directement aux méthodes du service
  get isConnected() {
    return this.getInstance().isConnected
  },
  get currentUser() {
    return this.getInstance().currentUser
  },
  get currentConversation() {
    return this.getInstance().currentConversation
  },

  // Méthodes principales
  connect: () => chatService.getInstance().connect(),
  disconnect: () => chatService.getInstance().disconnect(),
  createConversation: (...args) => chatService.getInstance().createConversation(...args),
  joinConversation: (...args) => chatService.getInstance().joinConversation(...args),
  leaveConversation: (...args) => chatService.getInstance().leaveConversation(...args),
  sendMessage: (...args) => chatService.getInstance().sendMessage(...args),
  fetchMessages: (...args) => chatService.getInstance().fetchMessages(...args),
  markAsRead: (...args) => chatService.getInstance().markAsRead(...args),
  startTyping: (...args) => chatService.getInstance().startTyping(...args),
  stopTyping: (...args) => chatService.getInstance().stopTyping(...args),
  getConversations: () => chatService.getInstance().getConversations(),
  getConversation: (id) => chatService.getInstance().getConversation(id),
  getCurrentConversation: () => chatService.getInstance().getCurrentConversation(),
  isConnectedToChat: () => chatService.getInstance().isConnectedToChat(),

  // Accès à l'instance complète si nécessaire
  get instance() {
    return this.getInstance()
  },

  // Méthodes utilitaires
  isDevelopmentMode: () => chatService.getInstance().isDevelopmentMode(),
  hasRealBackend: () => chatService.getInstance().hasRealBackend()
}

// Export pour utilisation dans les composants
export default chatService